// src/pages/admin/PerformanceGlance.jsx
import { useEffect, useMemo, useState } from "react";
import { Card, PaginatedTable, CheckboxField } from "@common";
import {
  performanceColumns,
  practitionerBreakdownColumns,
  costAndCollectionBreakdownColumns,
} from "@utils";

export default function PerformanceGlance({
  date,
  clinicId, // not used right now but keeping for future filters
  reportTotalData,
  hiddenColumns = [],
  page = 1,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
}) {
  const [monthIso] = useState(() => (date || "").slice(0, 7));

  const [showPractitioner, setShowPractitioner] = useState(false);
  const [showCostCollection, setShowCostCollection] = useState(false);

  // ✅ only enable horizontal scroll when BOTH boxes are checked
  const enableHorizontalScroll = showPractitioner && showCostCollection;

  const sourceData = useMemo(() => {
    if (!reportTotalData) return [];

    if (showPractitioner) {
      return Array.isArray(reportTotalData.practitionerBreakdown)
        ? reportTotalData.practitionerBreakdown
        : [];
    }

    if (showCostCollection) {
      return Array.isArray(reportTotalData.costAndCollection)
        ? reportTotalData.costAndCollection
        : [];
    }

    return Array.isArray(reportTotalData.performance) ? reportTotalData.performance : [];
  }, [reportTotalData, showPractitioner, showCostCollection]);

  const monthRows = useMemo(
    () =>
      sourceData
        .filter((d) => (d.date || "").startsWith(monthIso))
        .sort((a, b) => (a.date < b.date ? 1 : -1)),
    [sourceData, monthIso],
  );

  const mtd = useMemo(
    () =>
      monthRows.reduce(
        (acc, r) => ({
          collection: acc.collection + Number(r.collection ?? 0),
          practitioner_collection:
            acc.practitioner_collection + Number(r.practitioner_collection ?? 0),
          total_costs: acc.total_costs + Number(r.total_costs ?? 0),
          practitioner_production:
            acc.practitioner_production + Number(r.practitioner_production ?? r.production ?? 0),
          production: acc.production + Number(r.production ?? 0),
          cash: acc.cash + Number(r.cash ?? 0),
          debit: acc.debit + Number(r.debit ?? 0),
          mastercard: acc.mastercard + Number(r.mastercard ?? 0),
          visa: acc.visa + Number(r.visa ?? 0),
          amex: acc.amex + Number(r.amex ?? 0),
          checks: acc.checks + Number(r.checks ?? 0),
          insurance_check: acc.insurance_check + Number(r.insurance_check ?? 0),
          personal_eft: acc.personal_eft + Number(r.personal_eft ?? 0),
          insurance_eft: acc.insurance_eft + Number(r.insurance_eft ?? 0),
          other: acc.other + Number(r.other ?? 0),
          lab_costs: acc.lab_costs + Number(r.lab_costs ?? 0),
          invisalign_costs: acc.invisalign_costs + Number(r.invisalign_costs ?? 0),
          material_costs: acc.material_costs + Number(r.material_costs ?? 0),
          hours_worked: acc.hours_worked + Number(r.hours_worked ?? 0),
        }),
        {
          collection: 0,
          practitioner_collection: 0,
          total_costs: 0,
          practitioner_production: 0,
          production: 0,
          cash: 0,
          debit: 0,
          mastercard: 0,
          visa: 0,
          amex: 0,
          checks: 0,
          insurance_check: 0,
          personal_eft: 0,
          insurance_eft: 0,
          other: 0,
          lab_costs: 0,
          invisalign_costs: 0,
          material_costs: 0,
          hours_worked: 0,
        },
      ),
    [monthRows],
  );

  const mtdRow = useMemo(
    () => ({
      date: "MTD Total",
      practitioner_id: null,
      practitioner_name: "",
      practitioner_type: "",
      category: "",
      collection: mtd.collection,
      practitioner_collection: mtd.practitioner_collection,
      total_costs: mtd.total_costs,
      practitioner_production: mtd.practitioner_production,
      production: mtd.production,
      cash: mtd.cash,
      debit: mtd.debit,
      mastercard: mtd.mastercard,
      visa: mtd.visa,
      amex: mtd.amex,
      checks: mtd.checks,
      insurance_check: mtd.insurance_check,
      personal_eft: mtd.personal_eft,
      insurance_eft: mtd.insurance_eft,
      other: mtd.other,
      lab_costs: mtd.lab_costs,
      invisalign_costs: mtd.invisalign_costs,
      material_costs: mtd.material_costs,
      hours_worked: mtd.hours_worked,
      isMTDRow: true,
    }),
    [mtd],
  );

  useEffect(() => {
    onPageChange?.(1);
  }, [monthIso, pageSize, onPageChange]);

  const columns = useMemo(() => {
    const baseColumns = performanceColumns();

    if (showPractitioner && showCostCollection) {
      return [
        ...baseColumns,
        ...practitionerBreakdownColumns(),
        ...costAndCollectionBreakdownColumns(hiddenColumns),
      ];
    }
    if (showPractitioner) return [...baseColumns, ...practitionerBreakdownColumns()];
    if (showCostCollection)
      return [...baseColumns, ...costAndCollectionBreakdownColumns(hiddenColumns)];
    return baseColumns;
  }, [showPractitioner, showCostCollection, hiddenColumns]);

  const total = monthRows.length;

  return (
    // ✅ Prevent ANY page-level horizontal scrolling from this section
    <div className="w-full overflow-x-hidden">
      <Card>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Performance at a Glance</h3>
          <div className="mt-2 flex flex-col gap-2">
            <CheckboxField
              label="Show Practitioner Breakdown"
              checked={showPractitioner}
              onChange={setShowPractitioner}
            />
            <CheckboxField
              label="Show Cost & Collection Breakdown"
              checked={showCostCollection}
              onChange={setShowCostCollection}
            />
          </div>
        </div>

        <Card bodyClassName="p-0">
          {/* ✅ Scroll ONLY when both are checked */}
          <div
            className={
              enableHorizontalScroll ? "max-w-full overflow-x-auto" : "max-w-full overflow-x-hidden"
            }
          >
            {/* ✅ Only force wide table when scrolling is enabled */}
            <div className={enableHorizontalScroll ? "min-w-max" : "min-w-0"}>
              <PaginatedTable
                mode="client"
                columns={columns}
                data={monthRows}
                footerRow={mtdRow}
                reserveFooterRow={true}
                rowKey={(r, i) => (r.isMTDRow ? "mtd-total" : `${r.date}-${i}`)}
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                pageSizeOptions={[10, 25, 50, 100, 200]}
                rowClassName={(row) =>
                  row.isMTDRow
                    ? "bg-smiles-brightGreen/30 border-t-2 border-smiles-brightGreen/30"
                    : "hover:bg-palette-indigo-soft transition-colors"
                }
              />
            </div>
          </div>
        </Card>
      </Card>
    </div>
  );
}
